import { authHeader } from "./token";
import { tableCodeHeader } from "./tableAccess";
import { syncServerClock } from "../utils/serverClock";
import { BASE_API, ORDER_STATUS } from "../utils/constants";

export async function getOrdersByTableApi(idTable, status = "", ordering = "", token) {
  try {
    const tableFilter = `table=${idTable}`;
    const statusFilter = `status=${status}`;
    const closeFilter = "close=False";

    const url = `${BASE_API}/api/orders/?${tableFilter}&${statusFilter}&${closeFilter}&${ordering}`;
    const response = await fetch(url, {
      cache: "no-store",
      headers: { ...authHeader(token), ...tableCodeHeader() },
    });
    syncServerClock(response);
    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
}

// Todos los pedidos abiertos del local (todas las mesas) — pantalla de cocina.
export async function getOpenOrdersApi(token) {
  try {
    const url = `${BASE_API}/api/orders/?close=False&ordering=created_at`;
    const response = await fetch(url, {
      cache: "no-store",
      headers: { ...authHeader(token) },
    });
    syncServerClock(response);
    if (!response.ok) throw new Error(`No se pudieron leer los pedidos (${response.status})`);
    return await response.json();
  } catch (error) {
    throw error;
  }
}

export async function markOrderReadyApi(id, token) {
  try {
    const url = `${BASE_API}/api/orders/${id}/`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: { ...authHeader(token), "Content-Type": "application/json" },
      body: JSON.stringify({ status: ORDER_STATUS.READY }),
    });
    if (!response.ok) throw new Error(`No se pudo marcar como listo (${response.status})`);
    return await response.json();
  } catch (error) {
    throw error;
  }
}

export async function checkDeliveredOrderApi(id, token) {
  try {
    const url = `${BASE_API}/api/orders/${id}/`;
    const params = {
      method: "PATCH",
      headers: {
        ...authHeader(token),
        ...tableCodeHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        status: ORDER_STATUS.DELIVERED,
      }),
    };

    const response = await fetch(url, params);
    const result = await response.json();
    if (!response.ok) {
      // El servidor rechaza entregar lo que cocina aún no marca "Listo".
      throw new Error(result?.status?.[0] || `No se pudo marcar como entregado (${response.status})`);
    }
    return result;
  } catch (error) {
    throw error;
  }
}

export async function addOrderToTableApi(idTable,
  idProduct,
  note = "",
  optionChoice = "",
  extras = [],
  price = null,
  offerId = null,
  token,
  staffOptions = {}) {
  try {
    const url = `${BASE_API}/api/orders/`;
    const params = {
      method: "POST",
      headers: {
        ...authHeader(token),
        ...tableCodeHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        status: ORDER_STATUS.PENDING,
        table: idTable,
        product: idProduct,
        note: note || "",
        option_choice: optionChoice || "",
        extras: extras || [],
        price,
        offer: offerId || null,
        // Opciones del mozo (el backend las ignora si no hay sesión).
        takeaway: !!staffOptions.takeaway,
        is_courtesy: !!staffOptions.isCourtesy,
        courtesy_reason: staffOptions.courtesyReason || "",
        courtesy_value: staffOptions.isCourtesy ? staffOptions.courtesyValue : null,
      }),
    };
    const response = await fetch(url, params);
    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`No se pudo crear el pedido: ${errorBody}`);
    }
  } catch (error) {
    throw error;
  }
}

export async function addPaymentToOrderApi(idOrder, idPayment, token) {
  try {
    const url = `${BASE_API}/api/orders/${idOrder}/`;
    const params = {
      method: "PATCH",
      headers: {
        ...authHeader(token),
        ...tableCodeHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        payment: idPayment,
      }),
    };
    await fetch(url, params);
  } catch (error) {
    throw error;
  }
}

export async function closeOrderApi(idOrder, token) {
  try {
    const url = `${BASE_API}/api/orders/${idOrder}/`;
    const params = {
      method: "PATCH",
      headers: {
        ...authHeader(token),
        ...tableCodeHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        close: true,
      }),
    };
    await fetch(url, params);
  } catch (error) {
    throw error;
  }
}

// Cancelar un pedido: el backend lo borra pero antes lo registra con el
// motivo (OrderCancellation) — por query param, no en el cuerpo del DELETE.
export async function deleteOrderApi(idOrder, token, reason = "OTHER", note = "") {
  try {
    const query = new URLSearchParams({ reason, note }).toString();
    const url = `${BASE_API}/api/orders/${idOrder}/?${query}`;
    await fetch(url, { method: "DELETE", headers: { ...authHeader(token), ...tableCodeHeader() } });
  } catch (error) {
    throw error;
  }
}

export async function getOrdersInRangeApi(dateFrom, dateTo, token) {
  try {
    const dateFromFilter = dateFrom ? `&date_from=${dateFrom}` : "";
    const dateToFilter = dateTo ? `&date_to=${dateTo}` : "";

    const url = `${BASE_API}/api/orders/?ordering=created_at${dateFromFilter}${dateToFilter}`;
    const response = await fetch(url, { headers: { ...authHeader(token), ...tableCodeHeader() } });
    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
}

// Platos de cortesía del rango (control: Anulaciones y descuentos).
export async function getCourtesiesApi(dateFrom, dateTo, token) {
  const url = `${BASE_API}/api/orders/?is_courtesy=true&date_from=${dateFrom}&date_to=${dateTo}&ordering=-created_at`;
  const response = await fetch(url, { headers: { ...authHeader(token) } });
  if (!response.ok) throw new Error(`No se pudieron leer las cortesías (${response.status})`);
  return await response.json();
}

export async function getCancellationsApi(dateFrom, dateTo, token) {
  try {
    const url = `${BASE_API}/api/order-cancellations/?date_from=${dateFrom}&date_to=${dateTo}`;
    const response = await fetch(url, { headers: { ...authHeader(token) } });
    if (!response.ok) throw new Error(`No se pudieron leer las anulaciones (${response.status})`);
    return await response.json();
  } catch (error) {
    throw error;
  }
}

export async function getOrdersByPaymentApi(idPayment, token) {
  try {
    const paymentFilter = `payment=${idPayment}`;

    const url = `${BASE_API}/api/orders/?${paymentFilter}`;
    const response = await fetch(url, { headers: { ...authHeader(token), ...tableCodeHeader() } });
    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
}
